import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout, COMPANY } from "@/components/site/LegalLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ_ITEMS: { value: string; question: string; answer: string }[] = [
  {
    value: "aansprakelijkheid",
    question: "Aansprakelijkheid",
    answer: `Hoewel ${COMPANY.name} de grootst mogelijke zorgvuldigheid betracht bij het samenstellen en onderhouden van deze website, kan ${COMPANY.name} niet garanderen dat de informatie altijd volledig, juist en actueel is. De informatie op de website over elektrotechnische installaties, laadpalen, groepenkasten, camerabewaking en overige diensten is bedoeld als algemene voorlichting en vormt geen bindend advies voor een specifieke situatie. Voor een gedegen advies op locatie verwijzen wij u naar een schouw of offerte-aanvraag. ${COMPANY.name} aanvaardt geen aansprakelijkheid voor enige directe of indirecte schade die voortvloeit uit of in enig opzicht verband houdt met het gebruik van deze website.`,
  },
  {
    value: "intellectueel-eigendom",
    question: "Intellectueel eigendom",
    answer: `Alle rechten met betrekking tot inhoud, vormgeving, logo's, foto's, teksten en merken op deze website berusten bij ${COMPANY.name} of haar licentiegevers. Zonder uitdrukkelijke schriftelijke toestemming is het niet toegestaan deze inhoud geheel of gedeeltelijk te kopiëren, openbaar te maken, te verveelvoudigen of op andere wijze te gebruiken voor commerciële doeleinden.`,
  },
  {
    value: "hyperlinks",
    question: "Hyperlinks naar websites van derden",
    answer: `Deze website kan hyperlinks bevatten naar websites van derden, zoals fabrikanten, leveranciers of brancheorganisaties. ${COMPANY.name} heeft geen invloed op de inhoud van deze externe websites en is niet verantwoordelijk voor de daarop aangeboden informatie, producten of diensten. Het gebruik van dergelijke externe websites is voor eigen risico van de bezoeker.`,
  },
  {
    value: "productinformatie",
    question: "Productinformatie en beschikbaarheid",
    answer: `Productbeschrijvingen, specificaties, afbeeldingen en beschikbaarheid van artikelen kunnen zonder voorafgaande aankondiging worden aangepast door de fabrikant of leverancier. ${COMPANY.name} spant zich in om de gepubliceerde informatie zo actueel en volledig mogelijk te houden, maar kan afwijkingen ten opzichte van de daadwerkelijke levering niet volledig uitsluiten. Bij twijfel adviseren wij vooraf contact op te nemen met onze klantenservice.`,
  },
  {
    value: "wijzigingen",
    question: "Wijzigingen",
    answer: `${COMPANY.name} behoudt zich het recht voor de op deze website aangeboden informatie, waaronder de tekst van deze disclaimer, zonder voorafgaande aankondiging te wijzigen. De meest actuele versie van de disclaimer is altijd via deze pagina te raadplegen.`,
  },
  {
    value: "toepasselijk-recht",
    question: "Toepasselijk recht",
    answer:
      "Op deze disclaimer en op elk gebruik van deze website is Nederlands recht van toepassing. Geschillen die voortvloeien uit of samenhangen met het gebruik van deze website worden bij uitsluiting voorgelegd aan de bevoegde rechter in Nederland.",
  },
  {
    value: "contact",
    question: "Contact",
    answer: `Heeft u vragen over deze disclaimer of over het gebruik van de website? Neem dan contact op met ${COMPANY.name} via ${COMPANY.email} of ${COMPANY.phone}.`,
  },
];

export const Route = createFileRoute("/disclaimer")({
  head: () => ({
    meta: [
      { title: "Disclaimer Elvion Group | Aansprakelijkheid & gebruik website" },
      {
        name: "description",
        content:
          "Disclaimer van Elvion Group over het gebruik van de website, aansprakelijkheid, intellectueel eigendom en verwijzingen naar websites van derden.",
      },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Disclaimer Elvion Group | Aansprakelijkheid & gebruik website" },
      {
        property: "og:description",
        content:
          "Lees de disclaimer van Elvion Group: informatie over aansprakelijkheid, intellectueel eigendom, hyperlinks naar derden en wijzigingen aan de website.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://elvion-group.nl/disclaimer" },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/disclaimer" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://elvion-group.nl/#organization",
              name: COMPANY.name,
              legalName: COMPANY.legalName,
              url: COMPANY.website,
              address: {
                "@type": "PostalAddress",
                streetAddress: COMPANY.address,
                addressLocality: "Wageningen",
                postalCode: "6702DC",
                addressCountry: "NL",
              },
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "+31850806695",
                contactType: "customer service",
                email: COMPANY.email,
                availableLanguage: ["Dutch"],
              },
            },
            {
              "@type": "WebPage",
              "@id": "https://elvion-group.nl/disclaimer/#webpage",
              url: "https://elvion-group.nl/disclaimer",
              name: "Disclaimer Elvion Group | Aansprakelijkheid & gebruik website",
              description:
                "Disclaimer van Elvion Group over het gebruik van de website, aansprakelijkheid, intellectueel eigendom en verwijzingen naar websites van derden.",
              inLanguage: "nl-NL",
              isPartOf: { "@id": "https://elvion-group.nl/#organization" },
              publisher: { "@id": "https://elvion-group.nl/#organization" },
              dateModified: "2026-06-20",
              mainEntity: { "@id": "https://elvion-group.nl/disclaimer/#faq" },
            },
            {
              "@type": "BreadcrumbList",
              "@id": "https://elvion-group.nl/disclaimer/#breadcrumb",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://elvion-group.nl/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Disclaimer",
                  item: "https://elvion-group.nl/disclaimer",
                },
              ],
            },
            {
              "@type": "FAQPage",
              "@id": "https://elvion-group.nl/disclaimer/#faq",
              inLanguage: "nl-NL",
              isPartOf: { "@id": "https://elvion-group.nl/disclaimer/#webpage" },
              mainEntity: FAQ_ITEMS.map((item) => ({
                "@type": "Question",
                name: item.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: item.answer,
                },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <LegalLayout title="Disclaimer">
      <p>
        Deze disclaimer is van toepassing op de website van {COMPANY.name} en op
        alle informatie, teksten, afbeeldingen en documenten die via deze
        website beschikbaar worden gesteld. Door gebruik te maken van de
        website stemt u in met de onderstaande voorwaarden.
      </p>

      <h2 className="text-xl font-semibold text-foreground">
        Onderwerpen in deze disclaimer
      </h2>
      <p className="text-sm text-muted-foreground">
        Klik op een onderwerp om het uit te klappen.
      </p>

      <Accordion type="single" collapsible className="w-full">
        {FAQ_ITEMS.map((item) => (
          <AccordionItem key={item.value} value={item.value}>
            <AccordionTrigger className="text-lg font-semibold text-primary text-left">
              {item.question}
            </AccordionTrigger>
            <AccordionContent>
              <p>{item.answer}</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </LegalLayout>
  ),
});
